// src/pages/NotificationAnalyticsPage.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Button,
  Divider,
  Avatar,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Sms as SmsIcon,
  Email as EmailIcon,
  Telegram as TelegramIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Notifications as NotificationsIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  PieChart as PieChartIcon,
  MedicalInformation as MedicalInformationIcon,
} from "@mui/icons-material";
import Chart from "react-apexcharts";
import { reportsApi } from "../services/api";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";

const NotificationAnalyticsPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [timeRange, setTimeRange] = useState(30);
  const [error, setError] = useState("");

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await reportsApi.getReminderAnalytics(timeRange);
      if (response.data?.success) {
        setAnalyticsData(response.data.report);
      }
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
      setError(t('notifications.failedToLoadAnalytics'));
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const successRate = analyticsData?.totalRemindersSent ?
    Math.round((analyticsData.delivered / analyticsData.totalRemindersSent) * 100) : 0;

  const trendChartOptions = React.useMemo(() => ({
    chart: {
      type: 'area',
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: true }
    },
    colors: [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.success.main],
    stroke: { curve: 'smooth', width: 3 },
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0 } },
    xaxis: {
      categories: analyticsData?.dailyTrend?.map(d => d.day) || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      axisBorder: { show: false }
    },
    grid: { show: true, strokeDashArray: 4, borderColor: theme.palette.divider },
    tooltip: { theme: theme.palette.mode },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      onItemClick: { toggleDataSeries: false }
    }
  }), [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.success.main, theme.palette.divider, theme.palette.mode, analyticsData?.dailyTrend]);

  const trendSeries = React.useMemo(() => [
    { name: 'SMS', data: analyticsData?.dailyTrend?.map(d => d.sms) || [0, 0, 0, 0, 0, 0, 0] },
    { name: 'Email', data: analyticsData?.dailyTrend?.map(d => d.email) || [0, 0, 0, 0, 0, 0, 0] },
    { name: 'Telegram', data: analyticsData?.dailyTrend?.map(d => d.telegram) || [0, 0, 0, 0, 0, 0, 0] }
  ], [analyticsData?.dailyTrend]);

  const distributionOptions = React.useMemo(() => ({
    labels: ['SMS', 'Email', 'Telegram'],
    colors: [theme.palette.primary.main, theme.palette.info.main, theme.palette.success.main],
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'TOTAL',
              formatter: () => analyticsData?.totalRemindersSent || 0
            }
          }
        }
      }
    },
    legend: {
      position: 'bottom',
      onItemClick: { toggleDataSeries: false }
    },
    stroke: { show: false }
  }), [theme.palette.primary.main, theme.palette.info.main, theme.palette.success.main, analyticsData?.totalRemindersSent]);

  const distributionSeries = React.useMemo(() => [
    analyticsData?.byChannel?.sms?.sent || 0,
    analyticsData?.byChannel?.email?.sent || 0,
    analyticsData?.byChannel?.telegram?.sent || 0
  ], [analyticsData?.byChannel]);

  if (loading && !analyticsData) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress /></Box>
      </Container>
    );
  }

  const kpiCards = [
    { icon: <NotificationsIcon />, label: t('notifications.totalReminders'), value: analyticsData?.totalRemindersSent || 0, color: theme.palette.primary.main },
    { icon: <TrendingUpIcon />, label: t('notifications.deliveryRate'), value: `${successRate}%`, color: theme.palette.success.main },
    { icon: <SmsIcon />, label: t('notifications.smsVolume'), value: analyticsData?.byChannel?.sms?.sent || 0, color: theme.palette.info.main },
    { icon: <TelegramIcon />, label: t('notifications.telegramHub'), value: analyticsData?.byChannel?.telegram?.sent || 0, color: theme.palette.success.main },
    { icon: <EmailIcon />, label: t('notifications.emailVolume'), value: analyticsData?.byChannel?.email?.sent || 0, color: theme.palette.warning.main },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6, px: 3 }}>
      {/* Premium Header */}
      <Box
        sx={{
          textAlign: "center",
          mb: 6,
          background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.primary.main} 100%)`,
          borderRadius: 4,
          py: 5,
          color: 'white',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <NotificationsIcon sx={{ fontSize: 64, mb: 2, opacity: 0.9 }} />
          <Typography variant="h3" fontWeight="900" gutterBottom sx={{ letterSpacing: '-1px' }}>
            {t('notifications.title')}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
            {t('notifications.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
      </Box>

      {/* KPI Section */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        {kpiCards.map((kpi, idx) => (
          <Grid item xs={12} sm={6} md={2.4} key={idx}>
            <Paper
              sx={{
                p: 3,
                textAlign: "center",
                background: `linear-gradient(135deg, ${alpha(kpi.color, 0.05)} 0%, ${alpha(kpi.color, 0.15)} 100%)`,
                border: `1px solid ${alpha(kpi.color, 0.2)}`,
                borderRadius: 4,
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-5px)', boxShadow: `0 10px 30px ${alpha(kpi.color, 0.2)}` }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, color: kpi.color }}>
                {React.cloneElement(kpi.icon, { sx: { fontSize: 24, mr: 1 } })}
                <Typography variant="subtitle2" color="textSecondary" fontWeight="600" sx={{ textTransform: 'uppercase' }}>
                  {kpi.label}
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="800" color={kpi.color}>{kpi.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        {/* Trend Activity */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.06)', height: '100%' }}>
            <Box sx={{ p: 4, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <TrendingUpIcon color="primary" />
                <Typography variant="h6" fontWeight="800">{t('notifications.growthTrends')}</Typography>
              </Box>
            </Box>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ height: 350, pt: 2 }}>
                <Chart id="notification-growth-trend" options={trendChartOptions} series={trendSeries} type="area" height="100%" />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Operational Performance */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.06)', height: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="subtitle2" color="textSecondary" fontWeight="700" sx={{ mb: 3, textTransform: 'uppercase' }}>
                {t('notifications.operationalHealth')}
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" fontWeight="600">{t('notifications.globalSuccessRate')}</Typography>
                  <Typography variant="body2" color="success.main" fontWeight="700">{successRate}%</Typography>
                </Box>
                <Box sx={{ width: '100%', height: 10, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 5 }}>
                  <Box sx={{ width: `${successRate}%`, height: '100%', bgcolor: theme.palette.success.main, borderRadius: 5 }} />
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 3, border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, textAlign: 'center' }}>
                    <Typography variant="caption" color="textSecondary" display="block">{t('notifications.delivered')}</Typography>
                    <Typography variant="h5" fontWeight="900" color="primary.main">{analyticsData?.delivered || 0}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ p: 3, bgcolor: alpha(theme.palette.error.main, 0.05), borderRadius: 3, border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`, textAlign: 'center' }}>
                    <Typography variant="caption" color="textSecondary" display="block">{t('notifications.failed')}</Typography>
                    <Typography variant="h5" fontWeight="900" color="error.main">{analyticsData?.failed || 0}</Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ mt: 4, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 3 }}>
                <Typography variant="body2" color="info.main" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon fontSize="small" /> {t('notifications.deliveryServiceStable')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Channel Distribution */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.06)', height: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="subtitle2" color="textSecondary" fontWeight="700" sx={{ mb: 3, textTransform: 'uppercase' }}>
                {t('notifications.channelAdoption')}
              </Typography>
              <Box sx={{ height: 320 }}>
                <Chart id="channel-adoption-donut" options={distributionOptions} series={distributionSeries} type="donut" height="100%" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Failures and Details */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} lg={8}>
          <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" fontWeight="800">{t('notifications.recentFailures')}</Typography>
                <WarningIcon color="error" />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.divider, 0.05) }}>
                      <TableCell sx={{ fontWeight: 700 }}>{t('notifications.patient')}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t('notifications.channel')}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t('notifications.issue')}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t('notifications.time')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(analyticsData?.recentFailures || []).map((failure, idx) => (
                      <TableRow key={idx}>
                        <TableCell>ID #{failure.patientId}</TableCell>
                        <TableCell>
                          <Chip label={failure.channel} size="small" variant="outlined"
                            color={failure.channel === 'sms' ? 'info' : 'success'} />
                        </TableCell>
                        <TableCell color="error.main">{failure.error}</TableCell>
                        <TableCell>{new Date(failure.timestamp).toLocaleTimeString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.06)', height: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" fontWeight="800" gutterBottom>{t('notifications.schedulerStatus')}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 3 }}>
                <ScheduleIcon color="success" />
                <Box>
                  <Typography variant="body2" fontWeight="700">{t('notifications.nextExecution')}</Typography>
                  <Typography variant="caption" color="textSecondary">{t('notifications.dailyAt')}</Typography>
                </Box>
              </Box>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="textSecondary">{t('notifications.activeRules')}</Typography>
                  <Typography variant="body2" fontWeight="500">{t('notifications.rule60Day')}</Typography>
                  <Typography variant="body2" fontWeight="500">{t('notifications.rule14Day')}</Typography>
                </Box>
                <Button variant="contained" fullWidth startIcon={<RefreshIcon />} onClick={fetchAnalytics}>
                  {t('notifications.runManualScan')}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Logic Explanation Note */}
      <Box sx={{ mt: 2, p: 3, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 4, border: `1px solid ${alpha(theme.palette.info.main, 0.1)}` }}>
        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 600, mb: 1 }}>
          <MedicalInformationIcon color="info" /> {t('notifications.howItWorks')}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.7 }}>
          {t('notifications.howItWorksDescription')}
        </Typography>
      </Box>
    </Container>
  );
};

export default NotificationAnalyticsPage;
